import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { spacing30, sizingSmall, colorTextNeutral400, borderRadiusMedium, borderWidthThick, colorTextAlertError } from '@ellucian/react-design-system/core/styles/tokens';
import { Typography, makeStyles } from '@ellucian/react-design-system/core';
import ApplicationFilteredSearch from './ApplicationFilteredSearch';
import get from 'lodash/get';
import toPath from 'lodash/toPath';
import identity from 'lodash/identity';
import head from 'lodash/head';
import { useIntl } from 'react-intl';
import { getRecordNotFoundAttributeForTable} from '../../Helpers/ApplicationFilteredSearchHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { validateField, setTouched, setValue } from '@nsr/react-form';

const useStyles = makeStyles(() => ({
    typography: {
        marginTop: spacing30
    },
    filteredSearchContainer: {
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderColor: colorTextNeutral400,
        padding: sizingSmall,
        borderRadius: borderRadiusMedium
    },
    filteredSearchContainerError: {
        borderStyle: 'solid',
        borderWidth: borderWidthThick,
        borderColor: colorTextAlertError,
        padding: sizingSmall,
        borderRadius: borderRadiusMedium
    },
    labelTypography: {
        paddingBottom: spacing30
    },

}));

const FormFilteredSearch = props => {
    const { formatMessage } = useIntl();
    const classes = useStyles();
    const {
        filteredSearch,
        possibleValues = [],
        fullWidth = true,
        helperText: helperTextProp,
        label,
        name: nameProp,
        required,
        requiredErrorTextId = '',
        onChange,
        onBlur: onBlurProp,
        codeFieldInfo
    } = props;

    const dispatch = useDispatch();
    const selectAlwaysShowError = useMemo(() => {
        return createSelector(
            state => get(state.form.meta, `forceShowErrors.${head(toPath(nameProp))}`),
            value => !!value
        );
    }, [nameProp]);

    const selectValue = useMemo(() => {
        return createSelector(
            state => get(state.form.values, toPath(nameProp)),
            value => value || '',
        );
    }, [nameProp]);

    const selectError = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, toPath(nameProp)),
            identity
        );
    }, [nameProp]);

    const selectTouched = useMemo(() => {
        return createSelector(
            state => get(state.form.touched, toPath(nameProp)),
            identity
        );
    }, [nameProp]);

    const value = useSelector(selectValue);
    const error = useSelector(selectError);
    const touched = useSelector(selectTouched);
    const alwaysShowError = useSelector(selectAlwaysShowError);

    const validate = value => {
        if (!required) {
            return undefined;
        }
        if (!value) {
            return formatMessage({ id: requiredErrorTextId });
        }
        return undefined;
    };

    useEffect(() => {
        dispatch(validateField({ name: nameProp, validate, value }));
    }, [dispatch, nameProp, validate, value]);

    const onAddRecord = useCallback((selectedRecord) => {
        dispatch(setValue({
            name: nameProp,
            value: selectedRecord?.id ?? null
        }));
        if (!touched) {
            dispatch(setTouched({ name: nameProp, value: true }));
        }
        onChange && onChange(selectedRecord);
    }, []);

    // this is only needed because of how we do section repetition, since the object is being passed by reference 
    // each individual question wont have its own question object 
    // so we need to figure out the current value by checking if the question has a value in formState and grab that from possibleValues
    const getCurrentValue = useCallback(() => {
        const id = value;
        if (id) {
            if (id.includes('_noRecordFound')){ //record not found attribute have _recordNotFound suffix (id will have more char than uuid)
                const result =  getRecordNotFoundAttributeForTable(filteredSearch.recordNotFoundAttribute, formatMessage, codeFieldInfo);
                return result;
            }
            else{
                return possibleValues?.values?.find(value => value?.id === id) ?? null;
            }
        }
        return null;
    }, [possibleValues, value, nameProp, codeFieldInfo]);

    const onBlur = useCallback(e => {
        if (!touched) {
            dispatch(setTouched({ name: nameProp, value: true }));
        }
        onBlurProp && onBlurProp(e);
    }, [dispatch, onBlurProp]);

    let hasError = false;
    let helperText = helperTextProp;
    if (error && (touched || alwaysShowError)) {
        hasError = true;
        helperText = error;
    }
    return (
        <div>
            <div className={hasError ? classes.filteredSearchContainerError : classes.filteredSearchContainer}>
                <Typography className={classes.labelTypography}>{required ? `${label} *` : label}</Typography>
                <ApplicationFilteredSearch
                    filteredSearch={filteredSearch}
                    currentValue={getCurrentValue()}
                    fullWidth={fullWidth}
                    label={label}
                    onAdd={onAddRecord}
                    onBlur={onBlur}
                    codeFieldInfo={codeFieldInfo}
                >
                </ApplicationFilteredSearch>
            </div>
            <Typography
                className={classes.typography}
                variant={'body3'}
                color={hasError ? 'error' : 'initial'}
            >
                {helperText || ''}
            </Typography>
        </div>
    );
};

FormFilteredSearch.propTypes = {
    filteredSearch: PropTypes.object,
    alwaysShowError: PropTypes.bool,
    fullWidth: PropTypes.bool,
    helperText: PropTypes.string,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    requiredErrorTextId: PropTypes.string,
    onChange: PropTypes.func,
    possibleValues: PropTypes.object,
    onBlur: PropTypes.func,
    codeFieldInfo: PropTypes.object,
};

export default FormFilteredSearch;